import React from "react";

import { trpc } from "@blis/trpc/react";

import { useView } from "@/features/view/components/ViewProvider";
import { rt, useRealtimeInvalidate } from "@/lib/realtime";

const maxCache = 100;

type AccessionListQueryOptions = {
  staleTime?: number;
};

export function useAccessionListQuery(options: AccessionListQueryOptions = {}) {
  const { filters, settings } = useView();

  const params = React.useMemo(
    () => ({
      take: settings.limit ?? 500,
      filter: filters,
    }),
    [settings.limit, filters],
  );

  const list = trpc.accession.list.all.useQuery(params, {
    staleTime: options.staleTime,
    trpc: {
      context: {
        skipBatch: true,
      },
    },
  });

  const utils = trpc.useUtils();
  useRealtimeInvalidate({
    tags: [rt.accessions()],
    targets: [
      () => {
        utils.accession.list.all.invalidate(params);
      },
    ],
  });

  // note: the below only caches accession details. It does not cache the other requests such as test orders or samples
  React.useEffect(() => {
    if (list.data && list.data.length > 0) {
      for (let i = 0; i < Math.min(list.data.length, maxCache); i++) {
        const acc = list.data[i];
        utils.accession.get.setData({ id: acc.id }, acc);
        utils.subjects.get.setData(acc.subjectId, acc.subject);
      }
    }
  }, [utils.accession.get, utils.subjects.get, list.data]);

  return list;
}
